import React from 'react'

// A window left open across a deploy still runs the old bundle. Its lazy imports point at
// chunk hashes the new deploy no longer serves (nginx answers /static/ misses with a real
// 404), so the import fails. The fix is to reload once onto the current build. The
// timestamp guard stops a reload loop when the failure is something a reload cannot fix
// (offline, a genuinely broken deploy): a second failure inside the window is rethrown
// and ChunkErrorBoundary shows a manual "Reload" instead.
export const CHUNK_RELOAD_KEY = 'vamos_chunk_reload_at'
export const CHUNK_RELOAD_WINDOW_MS = 60 * 1000

const CHUNK_ERROR_PATTERNS = [
  /Loading (CSS )?chunk [\w-]+ failed/i, // webpack
  /Failed to fetch dynamically imported module/i, // Chrome
  /Importing a module script failed/i, // Safari
  /error loading dynamically imported module/i, // Firefox
]

export function isChunkLoadError(error) {
  if (!error) return false
  if (error.name === 'ChunkLoadError') return true
  const message = String(error.message || '')
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

function readLastReload(storage) {
  try {
    return Number(storage.getItem(CHUNK_RELOAD_KEY)) || 0
  } catch (e) {
    return 0
  }
}

/**
 * Records a reload attempt and returns true when a reload is allowed, i.e. no reload has
 * happened within CHUNK_RELOAD_WINDOW_MS. Storage failures (private mode) mean "no reload":
 * without the guard a reload could loop.
 */
export function claimChunkReload(storage = window.sessionStorage, now = Date.now()) {
  if (now - readLastReload(storage) < CHUNK_RELOAD_WINDOW_MS) return false
  try {
    storage.setItem(CHUNK_RELOAD_KEY, String(now))
    return true
  } catch (e) {
    return false
  }
}

/**
 * Wraps an import factory so a stale-chunk failure triggers one full page reload.
 * Exported separately from lazyWithRetry so it can be tested without React.lazy.
 */
export function retryImport(factory, { reload = () => window.location.reload(), storage } = {}) {
  return factory().catch((error) => {
    if (isChunkLoadError(error) && claimChunkReload(storage)) {
      reload()
      // Keep Suspense showing its spinner while the page reloads.
      return new Promise(() => {})
    }
    throw error
  })
}

/** Drop-in replacement for React.lazy. */
export default function lazyWithRetry(factory) {
  return React.lazy(() => retryImport(factory))
}
